import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { Flame, Gauge, TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import FuelModal from "../components/FuelModal";

export default function Dashboard() {
    const [metrics, setMetrics] = useState(null);
    const [showFuel, setShowFuel] = useState(false);

    const load = async () => {
        const { data } = await api.get("/dashboard/metrics");
        setMetrics(data);
    };

    useEffect(() => {
        load();
    }, []);

    const simulate = async () => {
        await api.post("/dashboard/simulate");
        toast.success("Demo metrics seeded.");
        load();
    };

    if (!metrics) {
        return <div className="text-muted-foreground">Loading transparency data...</div>;
    }

    const chartData = (metrics.monthly || []).map((m) => ({
        month: m.month,
        fuel: m.ad_spend,
        velocity: m.net_revenue,
        yield: m.net_revenue - m.ad_spend,
    }));

    const hasData = chartData.length > 0;

    return (
        <div className="space-y-8">
            <div className="flex items-start justify-between flex-wrap gap-4 fade-up">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">Transparency Dashboard</div>
                    <h1 className="font-heading text-4xl sm:text-5xl mt-1 tracking-tighter">Fuel vs Velocity</h1>
                    <p className="text-muted-foreground mt-2 max-w-2xl">
                        Every dollar of ad-spend (Fuel) alongside every dollar of net revenue (Velocity).
                        Your covenant splits, visualised.
                    </p>
                </div>
                <button
                    onClick={simulate}
                    data-testid="btn-simulate-metrics"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[hsl(40_50%_35%/0.4)] text-sm hover:bg-[hsl(151_55%_15%)] transition-colors"
                >
                    <Sparkles size={14} /> Seed Demo Metrics
                </button>
                <button
                    onClick={() => setShowFuel(true)}
                    data-testid="btn-fuel-vessel"
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                >
                    <Flame size={14} /> Fuel Site
                </button>
            </div>

            {showFuel && (
                <FuelModal territories={metrics.territories || []} onClose={() => { setShowFuel(false); load(); }} />
            )}

            {/* KPI Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <KPI icon={Flame} label="Fuel (Ad Spend)" value={money(metrics.total_ad_spend)} color="hsl(40 70% 55%)" />
                <KPI icon={Gauge} label="Velocity (Net Rev)" value={money(metrics.total_net_revenue)} color="hsl(153 50% 60%)" />
                <KPI icon={TrendingUp} label="Yield" value={money(metrics["yield"])} color="hsl(45 85% 68%)" />
                <KPI icon={Sparkles} label="Territories" value={metrics.territories?.length || 0} color="hsl(40 60% 50%)" />
            </div>

            {/* Main Chart */}
            <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="font-heading text-xl">Monthly performance</div>
                    <div className="flex items-center gap-4 text-xs">
                        <LegendItem color="hsl(40 70% 55%)" label="Fuel" />
                        <LegendItem color="hsl(153 50% 60%)" label="Velocity" />
                    </div>
                </div>
                <div className="h-80">
                    {hasData ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData}>
                                <defs>
                                    <linearGradient id="fuel" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="hsl(40 70% 55%)" stopOpacity={0.6} />
                                        <stop offset="100%" stopColor="hsl(40 70% 55%)" stopOpacity={0.05} />
                                    </linearGradient>
                                    <linearGradient id="vel" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="hsl(153 50% 60%)" stopOpacity={0.6} />
                                        <stop offset="100%" stopColor="hsl(153 50% 60%)" stopOpacity={0.05} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="hsl(151 40% 20%)" strokeDasharray="2 4" />
                                <XAxis dataKey="month" tick={{ fill: "hsl(151 10% 65%)", fontSize: 11 }} stroke="hsl(151 40% 20%)" />
                                <YAxis tick={{ fill: "hsl(151 10% 65%)", fontSize: 11 }} stroke="hsl(151 40% 20%)" tickFormatter={(v) => `$${v / 1000}k`} />
                                <Tooltip content={<ChartTip />} />
                                <Area type="monotone" dataKey="fuel" stroke="hsl(40 70% 55%)" fill="url(#fuel)" strokeWidth={2} />
                                <Area type="monotone" dataKey="velocity" stroke="hsl(153 50% 60%)" fill="url(#vel)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyChart label="No metrics yet — seed demo data above." />
                    )}
                </div>
            </div>

            {/* Split tracker */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="glass rounded-2xl p-6 lg:col-span-2">
                    <div className="font-heading text-xl mb-1">Monthly yield breakdown</div>
                    <div className="text-xs text-muted-foreground mb-4">Per-month surplus after Fuel is deducted from Velocity.</div>
                    <div className="h-60">
                        {hasData ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData}>
                                    <CartesianGrid stroke="hsl(151 40% 20%)" strokeDasharray="2 4" />
                                    <XAxis dataKey="month" tick={{ fill: "hsl(151 10% 65%)", fontSize: 11 }} stroke="hsl(151 40% 20%)" />
                                    <YAxis tick={{ fill: "hsl(151 10% 65%)", fontSize: 11 }} stroke="hsl(151 40% 20%)" tickFormatter={(v) => `$${v / 1000}k`} />
                                    <Tooltip content={<ChartTip />} />
                                    <Bar dataKey="yield" fill="hsl(45 85% 62%)" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <EmptyChart label="Seed demo metrics to view yield." />
                        )}
                    </div>
                </div>
                <div className="glass rounded-2xl p-6">
                    <div className="font-heading text-xl mb-1">Residual split</div>
                    <div className="text-xs text-muted-foreground mb-4">
                        {(metrics.share_config.partner * 100).toFixed(0)} / {(metrics.share_config.platform * 100).toFixed(0)} / {(metrics.share_config.director * 100).toFixed(0)}
                    </div>
                    <div className="space-y-5">
                        <SplitBar label="Partner" value={metrics.splits.partner} pct={metrics.share_config.partner} color="hsl(45 85% 62%)" />
                        <SplitBar label="Platform" value={metrics.splits.platform} pct={metrics.share_config.platform} color="hsl(153 50% 60%)" />
                        <SplitBar label="Director" value={metrics.splits.director} pct={metrics.share_config.director} color="hsl(25 70% 55%)" />
                        {metrics.splits.referrer > 0 && (
                            <SplitBar label="Referrer" value={metrics.splits.referrer} pct={metrics.share_config.director * metrics.share_config.referrer} color="hsl(280 50% 65%)" />
                        )}
                    </div>
                </div>
            </div>

            {/* Referral panel */}
            {metrics.referral && (
                <ReferralPanel referral={metrics.referral} />
            )}
        </div>
    );
}

function ReferralPanel({ referral }) {
    const link = `${window.location.origin}/register?ref=${referral.code}`;
    const copy = (txt) => {
        navigator.clipboard.writeText(txt);
        toast.success("Copied.");
    };
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="referral-panel">
            <div className="glass rounded-2xl p-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Your referral code</div>
                <div className="font-heading text-4xl mt-2 gold-gradient-text font-mono" data-testid="referral-code">{referral.code}</div>
                <div className="text-xs text-muted-foreground mt-2">Earn {(referral.referral_share_rate * 100).toFixed(0)}% of every referred partner's Director share — recurring, in perpetuity.</div>

                <div className="mt-5 space-y-2">
                    <button
                        onClick={() => copy(referral.code)}
                        className="w-full text-xs px-3 py-2 rounded-md border border-[hsl(40_50%_35%/0.4)] hover:bg-[hsl(151_55%_15%)] transition-colors"
                        data-testid="copy-referral-code"
                    >
                        Copy code
                    </button>
                    <button
                        onClick={() => copy(link)}
                        className="w-full text-xs px-3 py-2 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                        data-testid="copy-referral-link"
                    >
                        Copy invite link
                    </button>
                </div>
            </div>

            <div className="glass rounded-2xl p-6 lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="font-heading text-xl">Referral earnings</div>
                        <div className="text-xs text-muted-foreground">Lifetime cut of referred partners' director share</div>
                    </div>
                    <div className="font-heading text-3xl gold-gradient-text" data-testid="referral-earnings-total">
                        ${Number(referral.earnings_total || 0).toLocaleString()}
                    </div>
                </div>
                {referral.referred_partners && referral.referred_partners.length > 0 ? (
                    <table className="w-full text-sm">
                        <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-[hsl(40_40%_30%/0.18)]">
                            <tr>
                                <th className="py-2 pr-4">Agency</th>
                                <th className="py-2 pr-4">Yield</th>
                                <th className="py-2 pr-4">You earned</th>
                            </tr>
                        </thead>
                        <tbody>
                            {referral.referred_partners.map((p, idx) => (
                                <tr key={idx} className="border-b border-[hsl(40_40%_30%/0.08)]" data-testid={`referred-${idx}`}>
                                    <td className="py-2 pr-4">{p.agency_name}<div className="text-xs text-muted-foreground">{p.email}</div></td>
                                    <td className="py-2 pr-4 font-mono">${p["yield"]?.toLocaleString()}</td>
                                    <td className="py-2 pr-4 font-mono text-[hsl(45_85%_68%)]">${p.earned?.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="text-sm text-muted-foreground border border-dashed border-[hsl(40_40%_30%/0.2)] rounded-md p-6 text-center">
                        No partners under your code yet. Share your invite link.
                    </div>
                )}
            </div>
        </div>
    );
}

function KPI({ icon: Icon, label, value, color }) {
    return (
        <div className="glass rounded-xl p-5 fade-up" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: color + "33", color }}>
                    <Icon size={16} strokeWidth={1.5} />
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
            </div>
            <div className="font-heading text-3xl tracking-tight">{value}</div>
        </div>
    );
}

function SplitBar({ label, value, pct, color }) {
    return (
        <div>
            <div className="flex items-baseline justify-between mb-1">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">{label} · {(pct * 100).toFixed(0)}%</span>
                <span className="font-mono text-sm">{money(value)}</span>
            </div>
            <div className="h-2 rounded-full bg-[hsl(151_55%_15%)] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.max(4, pct * 100)}%`, background: color }} />
            </div>
        </div>
    );
}

function LegendItem({ color, label }) {
    return <div className="flex items-center gap-2 text-muted-foreground"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />{label}</div>;
}

function ChartTip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="glass rounded-md px-3 py-2 text-xs">
            <div className="font-mono text-foreground mb-1">{label}</div>
            {payload.map((p) => (
                <div key={p.dataKey} className="flex items-center justify-between gap-3">
                    <span className="capitalize text-muted-foreground">{p.dataKey}</span>
                    <span className="font-mono" style={{ color: p.color }}>{money(p.value)}</span>
                </div>
            ))}
        </div>
    );
}

function EmptyChart({ label }) {
    return (
        <div className="h-full flex items-center justify-center text-sm text-muted-foreground border border-dashed border-[hsl(40_40%_30%/0.2)] rounded-md">
            {label}
        </div>
    );
}

function money(v) {
    const n = Number(v || 0);
    if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`;
    return `$${n.toFixed(0)}`;
}
